import { errorToast } from "@/v2-ui/toast"
import Selector from "@/v2-backoffice-client/components/selector"
import type { SelectorItemProps } from "@/v2-backoffice-client/components/selector"
import type { CustomerFragment } from "@/v2-backoffice-client/customer/__types__/customer.fragment"
import { useTenantCustomerAssignMutation } from "@/v2-backoffice-client/tenant/customer/add/tenant.customer.assign.mutation"
import { useCustomerTenantRemoveMutation } from "@/v2-backoffice-client/customer/tenant/remove/customer.tenant.remove.mutation"

export type TenantCustomerAddSelectorItemProps = Omit<
  SelectorItemProps,
  "value" | "children" | "onSelect" | "onDeselect"
> & {
  customer: CustomerFragment
  tenantId: number
  onSelect?(): void
  onDeselect?(): void
}

function TenantCustomerAddSelectorItem(
  props: TenantCustomerAddSelectorItemProps,
) {
  const { customer, tenantId, onSelect, onDeselect, ...selectorItemProps } =
    props

  const [assign, { loading: assigning, error: assignError }] =
    useTenantCustomerAssignMutation()

  const [removeTenant, { loading: removingTenant, error: removeError }] =
    useCustomerTenantRemoveMutation()

  const loading = assigning || removingTenant
  const error = assignError || removeError

  errorToast(error, {
    title: "Failed to update customer assignment",
  })

  return (
    <Selector.Item
      isLoading={loading}
      value={String(customer.Id)}
      onSelect={() => {
        // Tenant assignment still keys on the legacy HubSpot id; an
        // app-created customer must be linked to a company first.
        if (!customer.ExternalCustomerId) return
        assign({
          variables: {
            tenantId,
            externalCustomerId: customer.ExternalCustomerId,
          },
        })
        onSelect?.()
      }}
      onDeselect={() => {
        removeTenant({ variables: { customerId: 0, tenantId } })
        onDeselect?.()
      }}
      {...selectorItemProps}
    >
      <Selector.ItemText
        primary={customer.Name ?? "Unnamed"}
        secondary={
          customer.ExternalCustomerId
            ? `${customer.ExternalCustomerId} - ${customer.UserCount} users`
            : "Not linked to HubSpot — link a company before assigning"
        }
      />
    </Selector.Item>
  )
}

export default TenantCustomerAddSelectorItem
